import GoogleIcon from '@mui/icons-material/Google'; // Service - swg
import GroupsIcon from '@mui/icons-material/Groups'; // Service - domain
import InsertLinkIcon from '@mui/icons-material/InsertLink'; // Service - linkedsite
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom'; // Service - free
import NewspaperRoundedIcon from '@mui/icons-material/NewspaperRounded'; // Service - undefined / all others (fallback)
import PaidIcon from '@mui/icons-material/Paid'; // Service - "subscription" (blox)
import SystemSecurityUpdateGoodIcon from '@mui/icons-material/SystemSecurityUpdateGood'; // Service - iap
import UploadFileIcon from '@mui/icons-material/UploadFile'; // Service - csv
import { SvgIconProps } from '@mui/material';
import { forwardRef, memo } from 'react';
import { mergeDeep } from 'remeda';

/**
 * -------------------------------------
 * Subscription Icon Helper
 * NOTE: Leefalcon, Newzware, Mediaspan, DTI, VisionData, Brainworks
 * for now default to the Newspaper Icon and ask these vendors for SVGs.
 * -------------------------------------
 **/

type SvgIconMappingValue = {
  Component: React.ComponentType<SvgIconProps>;
  defaultProps: Partial<SvgIconProps>;
};

// Icons returned could be either MUI SVG's or <img> Box's
const serviceIconMap = new Map<string, SvgIconMappingValue>([
  [
    '*',
    {
      Component: NewspaperRoundedIcon,
      defaultProps: {},
    },
  ],
  [
    'subscription',
    {
      Component: PaidIcon,
      defaultProps: {},
    },
  ],
  [
    'csv',
    {
      Component: UploadFileIcon,
      defaultProps: {},
    },
  ],
  [
    'free',
    {
      Component: MeetingRoomIcon,
      defaultProps: {},
    },
  ],
  [
    'domain',
    {
      Component: GroupsIcon,
      defaultProps: {},
    },
  ],
  [
    'linkedsite',
    {
      Component: InsertLinkIcon,
      defaultProps: {},
    },
  ],
  [
    'iap',
    {
      Component: SystemSecurityUpdateGoodIcon,
      defaultProps: {},
    },
  ],
  [
    'swg',
    {
      Component: GoogleIcon,
      defaultProps: {},
    },
  ],
]);

export type SubscriptionIconHelperProps = {
  iconType?: string;
  spreadProps?: Partial<SvgIconProps>;
};

export const SubscriptionIconHelper = memo(
  forwardRef<SVGSVGElement, SubscriptionIconHelperProps>(
    ({ iconType, spreadProps = {} }: SubscriptionIconHelperProps, ref) => {
      // Retrieve icon data; if not found, use the fallback '*'
      const iconData = serviceIconMap.get(iconType ?? '*') ?? serviceIconMap.get('*');

      // If no icon data is found even after the fallback check, return null.
      if (!iconData) {
        return null;
      }

      const { Component: IconComponent, defaultProps } = iconData;

      // Render guard
      if (!IconComponent) {
        return null;
      }

      // Convert props to a simpler type for merging
      // Prevent "Type instantiation is excessively deep and possibly infinite" error with mergedeep
      const defaultPropsRecord = defaultProps as Record<string, unknown>;
      const spreadPropsRecord = spreadProps as Record<string, unknown>;

      const mergedProps = mergeDeep(
        defaultPropsRecord,
        spreadPropsRecord,
      ) as SvgIconProps; // Assert back to SvgIconProps

      return (
        <IconComponent
          ref={ref}
          id="subscription-service-icon-svg"
          className="subscription-service-icon-svg"
          {...mergedProps}
        />
      );
    },
  ),
);
SubscriptionIconHelper.displayName = 'SubscriptionIconHelper';
